import * as icons from '@mui/icons-material';
import { Chip, Tooltip, IconButton } from '@mui/material';
import { useApiUrl, useCustomMutation } from '@refinedev/core';
import { EditButton, List, ShowButton, CreateButton, useDataGrid } from '@refinedev/mui';
import { useParams } from 'react-router';
import { DataGrid, type GridColDef } from '../../../../components/DataGrid.tsx';
import { useColumnVisibilityModel } from '../../../../hooks/columnVisibilityModel.ts';
import { AccountWithMagazines } from '../../../../types/account.ts';
import { getStatusColor, getStatusLabel } from './utils.ts';

export const AccountList = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const apiUrl = useApiUrl();
  const { mutate, isLoading: isResending } = useCustomMutation();

  const { dataGridProps, filters, setFilters } = useDataGrid<AccountWithMagazines>({
    resource: `organization/${organizationId}/accounts`,
    meta: {
      organizationId,
    },
    queryOptions: {
      select({ data, total }) {
        return {
          data: data.map((row) => AccountWithMagazines.parse(row)),
          total,
        };
      },
    },
  });

  const handleResendEmail = (accountId: string, accountEmail: string) => {
    if (!window.confirm(`${accountEmail} にメールを再送しますか？`)) {
      return;
    }

    mutate({
      url: `${apiUrl}/organization/${organizationId}/accounts/${accountId}/resend-email`,
      method: 'post',
      values: {},
      successNotification: { message: 'メールを再送しました', type: 'success' },
    });
  };
  const columns: GridColDef<AccountWithMagazines>[] = [
    {
      type: 'actions',
      field: 'actions',
      headerName: '',
      maxWidth: 120,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: function render({ row }) {
        return (
          <>
            <EditButton hideText recordItemId={row.id} />
            <ShowButton hideText recordItemId={row.id} />
            <Tooltip title="アカウント招待メールを再送します">
              <IconButton
                size="small"
                onClick={() => handleResendEmail(row.id, row.email)}
                disabled={isResending}
              >
                <icons.EmailOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'id',
      headerName: 'アカウントID',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'name',
      headerName: '氏名',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'メールアドレス',
      minWidth: 250,
      flex: 1,
    },
    {
      type: 'boolean',
      field: 'isAdmin',
      headerName: '管理者',
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => (
        <Chip label={value ? 'ON' : 'OFF'} color={value ? 'success' : 'error'} size="small" />
      ),
    },
    {
      type: 'boolean',
      field: 'magazineOptions.nbl',
      headerName: 'NBL',
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          label={row.magazineOptions.nbl ? 'ON' : 'OFF'}
          color={row.magazineOptions.nbl ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      type: 'boolean',
      field: 'magazineOptions.shiryoban',
      headerName: '資料版',
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          label={row.magazineOptions.shiryoban ? 'ON' : 'OFF'}
          color={row.magazineOptions.shiryoban ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      type: 'boolean',
      field: 'magazineOptions.junkan',
      headerName: '旬刊',
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          label={row.magazineOptions.junkan ? 'ON' : 'OFF'}
          color={row.magazineOptions.junkan ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      type: 'singleSelect',
      field: 'status',
      headerName: '利用状況',
      minWidth: 150,
      sortable: false,
      valueOptions: ['active', 'inactive'],
      getOptionLabel(key) {
        if (typeof key !== 'string') return '';
        return getStatusLabel(key);
      },
      renderCell: ({ row }) => (
        <Chip label={getStatusLabel(row.status)} color={getStatusColor(row.status)} size="small" />
      ),
    },
    {
      type: 'boolean',
      field: 'isInvited',
      headerName: '招待状況',
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          label={row.isInvited ? '招待中' : '-'}
          color={row.isInvited ? 'warning' : 'default'}
          size="small"
        />
      ),
    },
    {
      type: 'dateTime',
      field: 'createdAt',
      headerName: '作成日時',
      display: 'flex',
      minWidth: 150,
      valueFormatter(_, { createdAt }) {
        if (!createdAt) return '-';
        return createdAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
      },
    },
    {
      field: 'createdBy',
      headerName: '作成者',
      display: 'flex',
      minWidth: 150,
      valueGetter(_, row) {
        return row.createdBy ?? '-';
      },
    },
    {
      type: 'dateTime',
      field: 'modifiedAt',
      headerName: '変更日時',
      display: 'flex',
      minWidth: 150,
      valueFormatter(_, { modifiedAt }) {
        if (!modifiedAt) return '-';
        return modifiedAt.toLocaleString('ja-JP').replace(/:\d{2}$/, '');
      },
    },
    {
      field: 'modifiedBy',
      headerName: '変更者',
      display: 'flex',
      minWidth: 150,
      valueGetter(_, row) {
        return row.modifiedBy ?? '-';
      },
    },
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] = useColumnVisibilityModel(
    `organization/${organizationId}/accounts`,
  );

  return (
    <List
      headerButtons={() => (
        <>
          <CreateButton>招待</CreateButton>
        </>
      )}
    >
      <DataGrid
        {...dataGridProps}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        filterDebounceMs={800}
        defaultFilterColumn="name"
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        pageSizeOptions={[25, 50, 90]}
      />
    </List>
  );
};
